import type { MouseEvent } from "react";
import { useEffect } from "react";
import { styled } from "styled-components";

import { ListIcon, ResetIcon, StyledModalText, SuggestionsIcon, UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import {
  fetchCoachmarksVisibilityStatus,
  fetchUsedCoachmarks,
  removeCoachmarksFromUsed,
  toggleCoachmarks,
} from "domains/coachmarks/actions";
import { ListButton } from "domains/coachmarks/components/ListButton";
import { getCoachmarksAreEnabled } from "domains/coachmarks/selectors";
import { pushModalScreen } from "domains/modal/actions";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import type { ModalContentType } from "domains/modal/types";

const StyledListIcon = styled(ListIcon)`
  width: 22px;
  height: 22px;
  margin-right: 1rem;
`;

const StyledResetIcon = styled(ResetIcon)`
  width: 26px;
  height: 26px;
  margin-right: 1rem;
`;

const StyledSuggestionsIcon = styled(SuggestionsIcon)`
  width: 26px;
  height: 26px;
  margin-right: 1rem;
`;

export const CoachmarksSettingsModal = () => {
  const dispatch = useDispatch();
  const coachmarksAreEnabled = useSelector(getCoachmarksAreEnabled);
  const translate = useTranslate();

  const handleOpenModal = (id: ModalContentType) => (event: MouseEvent) => {
    event.preventDefault();
    dispatch(pushModalScreen({ id }));
  };

  const handleResetCoachmarksButtonClick = (event: MouseEvent) => {
    event.preventDefault();
    dispatch(removeCoachmarksFromUsed());
    dispatch(
      pushModalScreen({
        id: "ConfirmResetCoachmarks",
      }),
    );
  };

  const handleToggleCoachmarksButtonClick = (event: MouseEvent) => {
    event.preventDefault();
    dispatch(toggleCoachmarks());
  };

  useEffect(() => {
    dispatch(fetchCoachmarksVisibilityStatus());
    dispatch(fetchUsedCoachmarks());
  }, [dispatch]);

  return (
    <>
      <ModalHeader title={translate("app.coachmarks.settings_modal.title")} />
      <StyledModalText style={{ fontWeight: "normal" }}>
        {translate("app.coachmarks.settings_modal.text")}
      </StyledModalText>
      <ListButton
        descriptionText={translate("app.coachmarks.settings_modal.view_all_coachmarks_button.description")}
        iconElement={<StyledListIcon />}
        onClick={handleOpenModal("AllCoachmarks")}
      >
        {translate("app.coachmarks.settings_modal.view_all_coachmarks_button.label")}
      </ListButton>
      <ListButton
        descriptionText={translate("app.coachmarks.settings_modal.reset_all_coachmarks_button.description")}
        iconElement={<StyledResetIcon />}
        onClick={handleResetCoachmarksButtonClick}
      >
        {translate("app.coachmarks.settings_modal.reset_all_coachmarks_button.label")}
      </ListButton>
      <ListButton
        descriptionText={translate("app.coachmarks.settings_modal.suggest_improvements_button.description")}
        iconElement={<StyledSuggestionsIcon />}
        href="mailto:intelligence@weforum.org"
      >
        {translate("app.coachmarks.settings_modal.suggest_improvements_button.label")}
      </ListButton>
      <UIButton
        onClick={handleToggleCoachmarksButtonClick}
        error={coachmarksAreEnabled ?? undefined}
        variant="filled"
        fullwidth
        style={{ marginTop: "1.5rem" }}
      >
        {translate(
          coachmarksAreEnabled
            ? "app.coachmarks.settings_modal.disable_coachmarks"
            : "app.coachmarks.settings_modal.enable_coachmarks",
        )}
      </UIButton>
    </>
  );
};
